<template>
    <PopupMain>
        <template #title>Edit the line</template>

        <form id="popup-form" class="no-wrap" @submit.prevent="applyChanges">
            Line length:
            <input ref="input" v-model="lineLength"/>
            &nbsp;(<b>{{ getFootsInFractions(lineLength) }}"</b>)
        </form>

        <LineRibsForm v-if="lineType === 'simpleLine'"
                      :lineStartingLength="lineStartingLength"
                      :lineEndingLength="lineEndingLength"
                      :ribsCount="ribsCount"
                      @setCount="setRibsCount"
        ></LineRibsForm>
        <div v-else-if="lineType === 'lineWithRibs'">
            <br>
            <span class="no-wrap">Starting length: {{ lineStartingLength }} &nbsp;(<b>{{ getFootsInFractions(lineStartingLength) }}"</b>)</span>
            <br>
            <span class="no-wrap">Ending length: {{ lineEndingLength }} &nbsp;(<b>{{ getFootsInFractions(lineEndingLength) }}"</b>)</span>
        </div>

        <div class="hide-size--wrap">
            <label>
                Hide the line length: <input type="checkbox" v-model="hideLineLength">
            </label>
        </div>

        <template #footer>
            <button type="button" @click="lineSizeFlip">Flip units</button>

            <button type="submit" form="popup-form">Apply</button>
        </template>
    </PopupMain>
</template>

<script>
    import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
    import PopupMain from "./PopupMain";
    import LineRibsForm from "./LineRibsForm";

    export default {
        name: "LinePopup",

        components: {
            LineRibsForm,
            PopupMain,
        },

        data() {
            return {
                lineLength: 0,
                lineLengthBeforeChange: 0,
                ribsCount: 0,
                hideLineLength: false,
            }
        },

        computed: {
            ...mapState('editPopup', [
                'currentPopup',
            ]),

            ...mapState('editor', [
                'points',
            ]),

            ...mapState('line', [
                'ribExtraLength',
            ]),

            ...mapGetters('editor', [
                'footToPx',
                'getPointIndexById',
                'getFootsInFractions',
                'fractionsDecimalRound',
            ]),

            ...mapGetters('line', [
                'getLineByPoints',
            ]),

            lineData() {
                return this.currentPopup.line;
            },

            lineType() {
                return this.lineData.type;
            },

            fromPoint() {
                return this.lineData.fromPoint;
            },

            toPoint() {
                return this.lineData.toPoint;
            },

            line() {
                return this.getLineByPoints(this.fromPoint, this.toPoint);
            },

            lineStartingLength() {
                // the line starting length include an ribExtraLength = 1/16" for each of the ribs
                let startingLength = +this.lineEndingLength + ( +this.ribsCount * this.ribExtraLength);

                return this.fractionsDecimalRound(startingLength);
            },

            lineEndingLength() {
                let lineLengthDiff = this.lineLength - this.lineLengthBeforeChange;

                let endingLength = this.line.lengthInFeet + lineLengthDiff;

                return this.fractionsDecimalRound(endingLength);
            },

            lineWithRibsPart() {
                return this.currentPopup.lineWithRibsPart || null;
            },

            lineWithRibsPartLength() {
                return this.lineWithRibsPart &&
                    (this.lineWithRibsPart.toPoint.x - this.lineWithRibsPart.fromPoint.x);
            },

            ribLinePoints() {
                return this.fromPoint.ribLinePoints || [];
            },

            ribsTotalNumber() {
                let pointsNumber = this.ribLinePoints.length;

                if (!pointsNumber) return 0;

                return pointsNumber / 2 - 1;
            },

        },

        methods: {
            ...mapMutations('line', [
                'setRibs',
                'setLineSizeFlip',
            ]),

            ...mapActions('editPopup', [
                'clearCurrentPopup',
            ]),

            ...mapActions('editor', [
                'setPoints',
            ]),

            ...mapActions('line', [
                'simpleLineLengthChange',
                'makeLineWithRibs',
            ]),

            closePopup() {
                this.clearCurrentPopup();
            },
            
            applyChanges() {
                let lineLengthDiff = 0;

                switch (this.lineType) {
                    case 'simpleLine':

                        // the difference between previous and current line lineLength
                        lineLengthDiff = (this.lineLength - this.line.lengthInFeet) * this.footToPx;

                        // check if need to make the line with ribs
                        if (this.ribsCount) {
                            this.makeLineWithRibs({
                                line: this.currentPopup.line,
                                ribsCount: this.ribsCount,
                                lineLengthInFoots: this.lineEndingLength,
                            });
                        }

                        break;

                    case 'lineWithRibs':

                        // the difference between previous and current lineWithRibs part length
                        lineLengthDiff = this.lineLength * this.footToPx - this.lineWithRibsPartLength;

                        // set points of the rib line parts
                        this.setLineWithRibsPointsChange(lineLengthDiff);

                        break;

                    default:
                        break;
                }

                // apply the line length change
                this.simpleLineLengthChange({ line: this.line, lineLengthDiff, hideLineLength: this.hideLineLength });

                this.closePopup();
            },

            setLineWithRibsPointsChange(lineLengthDiff) {
                const points = this.ribLinePoints;
                let changeFromPoint = this.lineWithRibsPart.toPoint;
                let changeFromIndex = points.findIndex(point => +point.id === +changeFromPoint.id);

                let newPoints = [];

                // change only points that follow the changed part of the line
                for (let i = 0; i < points.length; i++) {
                    if (i < changeFromIndex) {
                        newPoints[i] = points[i];
                    }
                    else {
                        let point = points[i];
                        point.x += lineLengthDiff;

                        newPoints[i] = point;
                    }
                }

                this.setRibs({ point: this.fromPoint, ribLinePoints: newPoints });
            },

            setRibsCount(value) {
                this.ribsCount = value;
            },

            lineSizeFlip() {
                let point = this.fromPoint;
                let flip = !point['sizeFlip'];

                this.setLineSizeFlip({ point, flip });

                this.closePopup();
            },

        },

        beforeMount() {
            // to work correctly with the input in the popup,
            // we need to create an internal variable and initialize its value
            let lineLength = 0;

            switch (this.lineType) {
                case 'simpleLine':
                    lineLength = this.line.lengthInFeet;

                    break;

                case 'lineWithRibs':
                    lineLength = this.lineWithRibsPartLength / this.footToPx;

                    this.ribsCount = this.ribsTotalNumber;

                    break;

                default:
                    break;
            }

            this.lineLength = this.fractionsDecimalRound(lineLength);

            // lineLengthBeforeChange use for calculate starting/ending line length with ribs before apply
            this.lineLengthBeforeChange = +this.lineLength;

            // apply the current state of the 'hidden'
            this.hideLineLength = this.fromPoint.lineLengthIsHidden;
        },

        mounted() {
            // input field auto-focus
            this.$refs.input?.focus();
        }

    }
</script>

<style scoped>
    input {
        width: 60px;
        outline: none;
    }

    input[type="checkbox"] {
        width: auto;
        margin: auto;
        vertical-align: middle;
    }

    .hide-size--wrap {
        margin: 15px 0 0;
    }
</style>